import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from "@angular/core";
import { AccordionComponent } from "@shared-ay/accordion/accordion.component";
import { AvatarModule, ButtonModule, DividerModule, IconModule } from "@jack-sparrow-libraries/ui-kit";
import { SvgCentredIconComponent, SvgIconModule } from "features";
import { NavigationService } from "@services/navigation.service";
import { MenuProfileItem } from "@core/layout/components/top-bar/components/menu-profile/models/menu-profile-section.model";
import { LinkUtils } from "@core/layout/utils/link.utils";
import { DojoRoutesEnum, NavigationBase } from "@core/layout/enums/layout-routes.enum";
import { EmotionEventsHelperService, StrutsActionType } from "@services/helpers/emotion-events-helper.service";
import { FullClient } from "@models/full-client.model";
import { MenuProfileIds } from "@core/layout/components/top-bar/components/menu-profile/enums/menu-profile-ids.enum";
import { MenuProfileUtils } from "@core/layout/components/top-bar/components/menu-profile/utils/menu-profile.utils";
import { FullClientNamePipe } from "@core/pipes/full-client-name.pipe";
import { FullClientIconPipe } from "@core/pipes/full-client-icon.pipe";
import { FullClientRolePipe } from "@core/pipes/full-client-role.pipe";
import { MenuProfileService } from "@core/layout/components/top-bar/components/menu-profile/services/menu-profile.service";
import { ClientDroits } from "@jack-sparrow-libraries/emotion-models";
import { EfactureAuthService } from "@jack-sparrow-libraries/facture-electronique";
import { AgatheYouCareService } from "@services/agathe-you-care.service";

@Component({
	selector: "cba-menu-profile-dropdown",
	templateUrl: "./menu-profile-dropdown.component.html",
	styleUrls: ["./menu-profile-dropdown.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		AccordionComponent,
		DividerModule,
		ButtonModule,
		AvatarModule,
		IconModule,
		SvgIconModule,
		SvgCentredIconComponent,
		FullClientNamePipe,
		FullClientIconPipe,
		FullClientRolePipe,
	],
})
export class MenuProfileDropdownComponent {
	private readonly efactureAuthService = inject(EfactureAuthService);
	private readonly agatheYouCareService = inject(AgatheYouCareService);
	readonly navigationService = inject(NavigationService);
	readonly menuProfileService = inject(MenuProfileService);

	readonly $fullClient = input.required<FullClient>({ alias: "fullClient" });
	readonly $loading = input.required<boolean>({ alias: "loading" });
	readonly $droits = input.required<ClientDroits>({ alias: "droits" });

	readonly $close = output({ alias: "close" });

	readonly $items = computed(() =>
		this.menuProfileService.generateItems(this.$fullClient(), this.agatheYouCareService.$isCare()),
	);
	readonly $activeMenuId = signal<MenuProfileIds | null>(null);

	readonly $usedMenuIds = computed(() => {
		const droits = this.$droits();
		return droits ? MenuProfileUtils.getIdsByClientDroits(droits) : [];
	});

	readonly $menuItems = computed(() => {
		const items = this.$items();
		if (!items) {
			return [];
		}
		return items
			.filter((item) => this.$usedMenuIds().includes(item.id as MenuProfileIds))
			.map((item) => {
				if ("items" in item) {
					const items = item.items?.filter((subItem) =>
						this.$usedMenuIds().includes(subItem.id as MenuProfileIds),
					);
					return { ...item, items };
				}
				return item;
			});
	});

	onAction(item: MenuProfileItem) {
		if ("url" in item) {
			const url = LinkUtils.replaceUrlParams(item.url, {
				idPs: this.$fullClient().idPs,
			});
			if (item.blank) {
				window.open(url, "_blank");
			} else {
				this.navigationService.navigateByUrl(url);
			}
		} else if ("action" in item) {
			item.action();
		} else {
			console.error("Action not found");
		}
		this.close();
	}

	redirectAbout() {
		const url = LinkUtils.generateLinkModel(NavigationBase.DOJO, [DojoRoutesEnum.OPTIONS, DojoRoutesEnum.A_PROPOS]);
		this.navigationService.navigateByUrl(url);
		this.close();
	}

	logout() {
		this.close();
		// Session e-facturation : son cookie `cba-auth` appartient au back efacture (HttpOnly,
		// autre domaine) — ni Struts ni cette app ne peuvent l'effacer, seul POST /auth/logout
		// le peut. On ATTEND sa reponse avant de laisser Struts demarrer sa deconnexion : la
		// navigation top-level qu'il declenche annulerait la requete, et l'utilisateur suivant
		// sur ce navigateur reprendrait la session e-facturation du precedent (acces a ses
		// factures). L'echec ne bloque pas : la deconnexion hote se poursuit.
		this.efactureAuthService.logout().subscribe({
			next: () => this.deconnecterHote(),
			error: () => this.deconnecterHote(),
		});
	}

	private deconnecterHote(): void {
		EmotionEventsHelperService.dispatchEvent({
			action: StrutsActionType.LOGOUT,
			payload: null,
		});
	}

	expanded(expanded: boolean, id: string) {
		this.$activeMenuId.set(expanded ? (id as MenuProfileIds) : null);
	}

	close() {
		this.$activeMenuId.set(null);
		this.$close.emit();
	}
}
